import React, { useEffect, useState } from "react";
import { ModalForm, useModalDispatch } from "collections/Modal";
import {
  base64UrlString,
  Field,
  FieldProps,
  Form,
  TextArea
} from "collections/Form";
import i18n, { useTranslation } from "utilities/i18n";
import { DISMISS, useNotification } from "collections/Notifications";
import { InstanceModel } from "utilities/licenseServerUtils";
import { useGetLicenseQuery } from "generated/graphql";
import { object } from "yup";
import {
  CancelButton,
  CopyToClipboardButton,
  DetailsLoader,
  SubmitButton
} from "elements";
import { PartialExcept } from "utilities/types";

const GetLicenseSchema = object().shape({
  responseValue: base64UrlString()
    .trim()
    .label(i18n.t("RESPONSE_CODE"))
});

export const GetLicenseModal: React.FC<{
  instances: Array<InstanceModel>;
}> = ({ instances }) => {
  const { t } = useTranslation();
  const closeModal = useModalDispatch();
  const [addNotification] = useNotification();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const first: PartialExcept<InstanceModel, "recordId"> =
    instances?.length > 0
      ? instances[0]
      : {
          recordId: ""
        };

  const { data, fetching, stale, error, executor } = useGetLicenseQuery({
    variables: {
      recordId: first.recordId
    },
    requestPolicy: "network-only"
  });

  const loading = fetching || stale;
  const responseValue = loading ? "" : data?.getLicense ?? "";
  const retry = loading ? false : !!error || !responseValue;

  const initialValues = {
    responseValue: responseValue
  };

  useEffect(() => {
    if (!loading) {
      if (error) {
        setErrorMessage(
          t("TRY_AGAIN_WITH_ERROR", {
            error: error.message
          })
        );

        addNotification({
          type: "error",
          dismiss: DISMISS.DEFAULT,
          message: t("GET_LICENSE_ERROR", {
            message: error.message
          })
        });

        return;
      }

      if (!responseValue) {
        setErrorMessage(t("TRY_AGAIN"));

        addNotification({
          type: "error",
          dismiss: DISMISS.DEFAULT,
          message: t("LICENSE_NOT_FOUND_ERROR")
        });

        return;
      }

      setErrorMessage(null);

      addNotification({
        type: "success",
        dismiss: DISMISS.DEFAULT,
        message: t("SUCCESSFULLY_RETRIEVED_LICENSE"),
        persistThroughNav: true
      });
    }

    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, responseValue]);

  const onSubmit = async () => {
    if (retry) {
      setErrorMessage(null);
      executor({
        requestPolicy: "network-only"
      });
      return;
    }

    closeModal();
  };

  return (
    <ModalForm
      title={t("GET_LICENSE")}
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={onSubmit}
      name={"GetLicenseModal"}
      validationSchema={GetLicenseSchema}
    >
      {({ values }) => {
        return (
          <Form>
            <DetailsLoader loading={loading} title={t("GETTING_LICENSE")}>
              <div className="form-content">
                {errorMessage ? (
                  <p className="font-red pt-1">{errorMessage}</p>
                ) : (
                  <>
                    <label htmlFor="editor">{t("RESPONSE_CODE")}</label>
                    <TextArea
                      id="editor"
                      name="responseValue"
                      errorAbove={true}
                      className="details"
                      inputClassName="no-resize rows-4"
                      readOnly={true}
                    />
                  </>
                )}
              </div>
            </DetailsLoader>
            <div className="btn-container">
              {loading || retry ? (
                <CancelButton onClick={closeModal} />
              ) : (
                <CopyToClipboardButton
                  name="responseValue"
                  notifyName={t("INSTANCE_LICENSE", {
                    name: first?.identifiableName
                  })}
                  getTextToCopy={() => values.responseValue}
                  disabled={!values.responseValue}
                />
              )}
              {!loading && (
                <Field name="submit">
                  {({ form: { isSubmitting, isValid } }: FieldProps) => (
                    <SubmitButton
                      isSubmitting={isSubmitting}
                      disabled={isSubmitting || !isValid}
                      labelKey={retry ? "RETRY" : "DONE"}
                      submittingLabelKey={retry ? "RETRY" : "DONE"}
                    />
                  )}
                </Field>
              )}
            </div>
          </Form>
        );
      }}
    </ModalForm>
  );
};
